import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { fmtDateTime } from '../utils/dateFormat';
import { confirm } from './ConfirmDialog';
import SearchSelect from './SearchSelect';

const fmt = (v) => Number(v || 0).toFixed(2);

// Yaşıl Bazarlıq qaiməsi: bax + (admin) sətir düzəlt / sətir sil / qaiməni sil.
// Bazarlıq bölməsində və Hesabatlar → «Yaşıl Bazarlıq»da eyni pəncərə işlədilir.
//   purchaseId — açılacaq qaimə; onClose — bağla; onChanged — dəyişiklikdən sonra (siyahını/hesabatı yenilə)
export default function PurchaseQaimeModal({ purchaseId, isAdmin, onClose, onChanged }) {
    const [purchase, setPurchase] = useState(null);
    const [editLine, setEditLine] = useState(null);
    const [elQty, setElQty] = useState('');
    const [elPrice, setElPrice] = useState('');
    const [elBusy, setElBusy] = useState(false);
    const [elIng, setElIng] = useState('');          // sətrin məhsulu (səhv seçilibsə dəyişmək üçün)
    const [ingredients, setIngredients] = useState(null); // məhsul siyahısı (ilk redaktədə yüklənir)

    const load = async () => {
        try {
            const res = await api.get(`/purchases/${purchaseId}`);
            setPurchase(res.data.data);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Qaimə tapılmadı.');
            onClose?.();
        }
    };
    useEffect(() => { if (purchaseId) load(); }, [purchaseId]); // eslint-disable-line react-hooks/exhaustive-deps

    const startEditLine = (it) => {
        setEditLine(it.id);
        setElQty(String(Number(it.qty)));
        setElPrice(String(Number(it.unit_price)));
        setElIng(String(it.ingredient_id));
        if (!ingredients) {
            api.get('/ingredients/all')
                .then((r) => setIngredients(r.data.data || []))
                .catch(() => setIngredients([]));
        }
    };
    const cancelEditLine = () => { setEditLine(null); setElQty(''); setElPrice(''); setElIng(''); };

    const saveLine = async (it) => {
        const qty = Number(String(elQty).replace(',', '.'));
        const price = Number(String(elPrice).replace(',', '.'));
        if (!Number.isFinite(qty) || qty <= 0) return toast.error('Miqdar düzgün deyil.');
        if (!Number.isFinite(price) || price < 0) return toast.error('Qiymət düzgün deyil.');
        setElBusy(true);
        try {
            const res = await api.put(`/purchases/${purchase.id}/items/${it.id}`, { qty, unit_price: price, ingredient_id: Number(elIng) || it.ingredient_id });
            toast.success(res.data?.msg || 'Sətir düzəldildi.');
            cancelEditLine();
            await load();
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta.');
        } finally { setElBusy(false); }
    };

    const deleteLine = async (it) => {
        if (!(await confirm({
            title: 'Sətri sil',
            message: `"${it.ingredient_name}" (${it.qty} ${it.unit}) qaimədən çıxarılsın? Stok ${it.qty} ${it.unit} geri çıxacaq, tədarükçü borcu azalacaq.`,
            confirmText: 'Sil', danger: true,
        }))) return;
        try {
            const res = await api.delete(`/purchases/${purchase.id}/items/${it.id}`);
            toast.success(res.data?.msg || 'Sətir silindi.');
            onChanged?.();
            if (res.data?.purchase_deleted) onClose?.();
            else await load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta.');
        }
    };

    const deletePurchase = async () => {
        if (!(await confirm({
            title: 'Qaiməni sil',
            message: `Qaime #${purchase.id} silinsin? Stok geri qaytarılacaq (−miqdar), tədarükçü borcu silinər.`,
            confirmText: 'Sil', danger: true,
        }))) return;
        try {
            await api.delete(`/purchases/${purchase.id}`);
            toast.success('Silindi, stok geri qaytarıldı.');
            onChanged?.();
            onClose?.();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta.');
        }
    };

    if (!purchase) return null;

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[80] p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-bold text-slate-800 text-lg">Qaime #{purchase.id}</h2>
                    <button onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 text-2xl leading-none w-8 h-8 flex items-center justify-center rounded hover:bg-slate-100">×</button>
                </div>
                <p className="text-sm text-slate-500 mb-1">Tarix: <b>{fmtDateTime(purchase.created_at)}</b></p>
                {purchase.supplier_name && (
                    <p className="text-sm text-slate-500 mb-3">Tədarükçü: <b>{purchase.supplier_name}</b></p>
                )}
                <div className="overflow-x-auto"><table className="w-full text-sm border border-slate-100 rounded-xl overflow-hidden min-w-[460px]">
                    <thead>
                        <tr className="bg-slate-50 text-slate-500 text-xs">
                            <th className="text-left px-3 py-2">Məhsul</th>
                            <th className="text-right px-3 py-2">Miqdar</th>
                            <th className="text-right px-3 py-2">Vahid ₼</th>
                            <th className="text-right px-3 py-2">Cəmi ₼</th>
                            {isAdmin && <th className="text-right px-3 py-2">Əməliyyat</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {(purchase.items || []).map((it, i) => (
                            (
                                <tr key={i} className={`border-t border-slate-100 ${editLine === it.id ? 'bg-amber-50' : ''}`}>
                                    <td className="px-3 py-2">{it.ingredient_name}</td>
                                    <td className="px-3 py-2 text-right">{it.qty} {it.unit}</td>
                                    <td className="px-3 py-2 text-right text-slate-500">{fmt(it.unit_price)}</td>
                                    <td className="px-3 py-2 text-right font-semibold">{fmt(it.total_price)}</td>
                                    {isAdmin && (
                                        <td className="px-2 py-2 text-right whitespace-nowrap">
                                            <button onClick={() => (editLine === it.id ? cancelEditLine() : startEditLine(it))} title="Düzəlt"
                                                className={`rounded-lg text-xs px-2 py-1 ${editLine === it.id ? 'bg-amber-200 text-amber-800' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}>✏️</button>
                                            <button onClick={() => deleteLine(it)} title="Sətri sil"
                                                className="ml-1 rounded-lg bg-red-50 text-red-600 text-xs px-2 py-1 hover:bg-red-100">🗑</button>
                                        </td>
                                    )}
                                </tr>
                            )
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="bg-green-50 font-bold">
                            <td colSpan="3" className="px-3 py-2 text-right text-slate-600">Cəmi:</td>
                            <td className="px-3 py-2 text-right text-green-700">{fmt(purchase.total)} ₼</td>
                            {isAdmin && <td></td>}
                        </tr>
                    </tfoot>
                </table></div>

                {/* Sətir düzəlişi — cədvəldən kənarda (açılan siyahı kəsilməsin, xanalar bir sırada) */}
                {editLine && (() => {
                    const it = (purchase.items || []).find((x) => x.id === editLine);
                    if (!it) return null;
                    const ingOpts = (ingredients || []).map((g) => ({ value: g.id, label: `${g.name}${g.depot_name ? ` · ${g.depot_name}` : ''}` }));
                    const selIng = (ingredients || []).find((g) => String(g.id) === String(elIng));
                    const unit = selIng?.unit || it.unit;
                    const changed = String(elIng) !== String(it.ingredient_id);
                    return (
                        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3">
                            <div className="mb-2 text-xs font-bold uppercase tracking-wide text-amber-700">Sətri düzəlt</div>
                            <label className="mb-1 block text-[11px] font-semibold text-slate-500">Məhsul</label>
                            {ingredients === null ? (
                                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-400">Yüklənir...</div>
                            ) : (
                                <SearchSelect value={elIng} onChange={setElIng} options={ingOpts} placeholder="Məhsul seçin" />
                            )}
                            {changed && (
                                <p className="mt-1 text-[11px] text-amber-700">
                                    «{it.ingredient_name}» stokundan {Number(it.qty)} çıxacaq, «{selIng?.name || ''}» stokuna yeni miqdar girəcək.
                                </p>
                            )}
                            <div className="mt-3 grid grid-cols-3 gap-2">
                                <div>
                                    <label className="mb-1 block text-[11px] font-semibold text-slate-500">Miqdar</label>
                                    <div className="flex h-10 items-center rounded-lg border border-slate-300 bg-white focus-within:border-amber-500">
                                        <input type="number" step="0.001" value={elQty} onChange={e => setElQty(e.target.value)}
                                            className="h-full w-full min-w-0 rounded-lg bg-transparent px-2 text-right text-sm focus:outline-none" />
                                        <span className="shrink-0 pr-2 text-xs text-slate-400">{unit}</span>
                                    </div>
                                </div>
                                <div>
                                    <label className="mb-1 block text-[11px] font-semibold text-slate-500">Vahid ₼</label>
                                    <input type="number" step="0.01" value={elPrice} onChange={e => setElPrice(e.target.value)}
                                        className="h-10 w-full rounded-lg border border-slate-300 bg-white px-2 text-right text-sm focus:border-amber-500 focus:outline-none" />
                                </div>
                                <div>
                                    <label className="mb-1 block text-[11px] font-semibold text-slate-500">Cəmi ₼</label>
                                    <div className="flex h-10 items-center justify-end rounded-lg bg-white/70 px-2 text-sm font-bold text-slate-700">
                                        {fmt((Number(elQty) || 0) * (Number(elPrice) || 0))}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3 flex justify-end gap-2">
                                <button onClick={cancelEditLine} disabled={elBusy}
                                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">Ləğv et</button>
                                <button onClick={() => saveLine(it)} disabled={elBusy || !elIng}
                                    className="rounded-lg bg-green-600 px-4 py-1.5 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-50">{elBusy ? 'Saxlanır...' : '✓ Yadda saxla'}</button>
                            </div>
                        </div>
                    );
                })()}

                <div className="mt-4 flex justify-end gap-2">
                    <button onClick={onClose}
                        className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50">Bağla</button>
                    {isAdmin && (
                        <button onClick={deletePurchase}
                            className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold">
                            🗑 Sil (stok geri qaytar)
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
